/**
 * Coverage AutoFill (CAF-4) — per-cell fill-source chip renderer.
 *
 * Reads a `RequirementCoverage` (CAF-1 schema, produced by the CAF-2
 * `resolveRequirementCoverage` resolver and persisted on
 * `site.coverage[reqId]` by the CAF-5 pipeline writer) and renders a
 * compact chip on each Coverage Matrix row showing WHICH tier filled
 * the row:
 *
 *   - `🤖 AI`            (yellow, with confidence %)  — `fillSource: 'ai-inference'`
 *   - `🌐 Provider · id` (blue)                       — `'provider'`
 *   - `📊 DAM baseline`  (purple)                     — `'dam-baseline'`
 *   - `📄 Doc`           (green)                      — `'doc'`
 *   - `✍ Authored`       (cyan)                       — `'authored'`
 *   - `— Missing`        (muted)                      — `'missing'`
 *
 * The chip carries the resolver's `rationale` + `sourceRef` in the
 * tooltip (hallucination guard HARD RULE 8 — every non-missing fill
 * carries provenance).
 *
 * When the cell wasn't filled by an uploaded doc AND the user hasn't
 * pinned an authored override, an `[Upgrade by uploading…]` CTA is
 * surfaced so users know they can strengthen any auto-filled row by
 * adding the real document — the same affordance the Coverage Matrix
 * exposes for missing rows, just contextualised for "we filled this
 * with X — give us the real doc to upgrade".
 *
 * Composition:
 *   - CAF-1 schema (`requirementCoverage.ts`) — the input contract.
 *   - CAF-2 resolver — produces the `RequirementCoverage` this chip
 *     reads; the chip never re-derives the fill source.
 *   - EVL-3-LABELS / EVL-6g — chip vocabulary mirrors the lineage
 *     strip emoji set so the matrix reads as a coherent design
 *     language. Tones are token-class only (no inline colours) so
 *     theme switches honour the chip palette.
 *
 * Test contract:
 *   - `data-testid={`fill-source-${reqId}`}` for the wrapper.
 *   - `data-tone={'ai' | 'provider' | 'dam' | 'doc' | 'authored' | 'missing'}`
 *     drives the per-fixture assertion (the test asserts ONE tone per
 *     fixture so a refactor that drops a tone is caught).
 *   - The `[Upgrade by uploading…]` button carries
 *     `data-testid={`fill-source-upgrade-${reqId}`}` so the test pins
 *     when it renders / when it's hidden.
 *
 * Out of scope:
 *   - Persisting the user's "ignore this fill" preference — the chip
 *     renders read-only state. The Author / Upload affordances on
 *     `BestStatusCell` are how the user changes the underlying state;
 *     the chip just SHOWS that state.
 */
import React from 'react';
import type {
  RequirementCoverage,
  RequirementCoverageFillSource,
} from '../../../lib/coverage/requirementCoverage';

/** Discriminated tone for the chip palette. Mirrors the spec verbatim
 *  so the renderer can lookup tone-class + label without a switch.
 *
 *  IIA-2 — `'doc-vision'` is the new sibling of `'doc'`: same green
 *  family but with the camera glyph + "Image (vision)" label so users
 *  can tell at a glance that this row was filled by an image
 *  extraction (Gemini Vision) rather than text/OCR. */
type ChipTone = 'ai' | 'provider' | 'dam' | 'doc' | 'doc-vision' | 'authored' | 'missing';

interface ChipMeta {
  tone: ChipTone;
  icon: string;
  label: string;
  /**
   * Tone palette class. Tokens only — no inline hex — so the theme
   * inversion (light/dark) keeps the chip readable. The palette mirrors
   * the EVL-3 chip strip: emerald for DAM, primary for provider, info
   * for authored, muted for missing. Yellow/AI uses the `warning` token
   * because the resolver caps AI confidence at 0.4 (R1 risk
   * mitigation — never inflate).
   */
  toneClass: string;
}

/**
 * Mapping from `RequirementCoverageFillSource` → chip metadata.
 *
 * NOTE: the `fillSource` strings are NOT used as chip tones directly
 * because the spec calls for tonal grouping (e.g. dam-baseline + dam
 * = same purple tone) — the indirection here gives the test a stable
 * `data-tone` enum to assert against.
 */
// Keyed as a plain string record (not `RequirementCoverageFillSource`)
// because IIA-2's `'doc-vision'` is a renderer-internal alias for
// `fillSource: 'doc'` + `extractionModality: 'vision'`, not a real
// FillSource enum member. The runtime lookup helper below picks
// between `'doc'` and `'doc-vision'` based on the modality field.
const FILL_SOURCE_CHIPS: Record<string, ChipMeta> = {
  'ai-inference': {
    tone: 'ai',
    icon: '🤖',
    label: 'AI',
    toneClass: 'border-warning/40 bg-warning/10 text-warning',
  },
  provider: {
    tone: 'provider',
    icon: '🌐',
    label: 'Provider',
    toneClass: 'border-primary/40 bg-primary/10 text-primary',
  },
  'dam-baseline': {
    tone: 'dam',
    icon: '📊',
    label: 'DAM baseline',
    toneClass: 'border-violet-500/40 bg-violet-500/10 text-violet-500',
  },
  doc: {
    tone: 'doc',
    icon: '📄',
    label: 'Doc',
    toneClass: 'border-success/40 bg-success/10 text-success',
  },
  // IIA-2 — vision-doc variant. Same fillSource value (`'doc'`) but
  // looked up via a separate key when the resolver stamped
  // `extractionModality: 'vision'`. Distinct icon (camera) + label
  // ("Image (vision)") so the user knows the row came from an image
  // extraction (capped at confidence 0.60 per pack §IIA-6 until a
  // second image OR CAR provider cross-check agrees).
  'doc-vision': {
    tone: 'doc-vision',
    icon: '📷',
    label: 'Image (vision)',
    toneClass: 'border-success/40 bg-success/10 text-success',
  },
  authored: {
    tone: 'authored',
    icon: '✍',
    label: 'Authored',
    toneClass: 'border-info/40 bg-info/10 text-info',
  },
  missing: {
    tone: 'missing',
    icon: '—',
    label: 'Missing',
    toneClass: 'border-border-subtle bg-state-hover text-text-muted',
  },
  // `unknown` is the legacy `CoverageFillSource` member from
  // `confidenceTier.ts` — RequirementCoverageFillSource extends with
  // 'missing', but the union still includes 'unknown' as the legacy
  // back-fill bucket. Render it identically to 'missing' so callers who
  // hand us a partially-projected envelope still get a readable chip.
  unknown: {
    tone: 'missing',
    icon: '—',
    label: 'Unknown',
    toneClass: 'border-border-subtle bg-state-hover text-text-muted',
  },
};

export interface CoverageCellFillSourceProps {
  /**
   * The resolver's per-requirement projection — produced by
   * `resolveRequirementCoverage` and stamped onto `site.coverage[reqId]`
   * by the CAF-5 pipeline writer. Optional so callers can mount the
   * component before the writer's first run (legacy sites): in that
   * case the chip renders the `'missing'` tone with a "no coverage
   * envelope yet" tooltip.
   */
  coverage?: RequirementCoverage;
  /**
   * Stable requirement id. Used by the chip's `data-testid` so the
   * QA harness can assert "this row's chip carries tone X".
   */
  reqId: string;
  /**
   * Fired when the user clicks `[Upgrade by uploading…]`. Receives the
   * `reqId` so the host can open the upload pre-tagged for the same
   * requirement (same plumbing as `onOpenUploadFor`). When omitted the
   * CTA is not rendered.
   */
  onUpgradeByUploading?: (reqId: string) => void;
}

/**
 * Decide whether to surface the `[Upgrade by uploading…]` CTA.
 *
 * Pack §CAF-4 rule:
 *   - Show when `fillSource !== 'doc'` AND `authoredOverride !== true`.
 *
 * Exported for the regression test so the rule isn't duplicated.
 */
export function shouldOfferUpgrade(coverage: RequirementCoverage): boolean {
  if (coverage.fillSource === 'doc') return false;
  if (coverage.authoredOverride) return false;
  // 'missing' rows also benefit from the upload affordance — the
  // existing Coverage Matrix has its own `[Upload]` button for the
  // missing case, but the chip-side CTA is consistent across tones.
  return true;
}

/**
 * Per-cell fill-source chip. See module docstring for the contract.
 */
export const CoverageCellFillSource: React.FC<CoverageCellFillSourceProps> = ({
  coverage,
  reqId,
  onUpgradeByUploading,
}) => {
  // No envelope yet (legacy site or CAF-5 writer hasn't run for this
  // requirement) — render the muted "missing" chip with an explicit
  // tooltip. Hallucination guard: we never invent a fill source.
  if (!coverage) {
    const meta = FILL_SOURCE_CHIPS.missing;
    return (
      <span
        data-testid={`fill-source-${reqId}`}
        data-tone={meta.tone}
        title="No coverage envelope persisted yet — rebuild SED or upload a document to populate this row."
        className={`inline-flex items-center gap-0.5 px-1 py-0.5 border type-body font-mono leading-none ${meta.toneClass}`}
      >
        <span aria-hidden="true">{meta.icon}</span>
        <span className="uppercase tracking-widest">{meta.label}</span>
      </span>
    );
  }

  // IIA-2 — switch the chip variant when the resolver stamped
  // `extractionModality: 'vision'` on a `'doc'` fill. Keeps the same
  // tonal family (green/success) but flips icon + label to
  // `📷 Image (vision)` so users can tell the source modality at a
  // glance. Non-doc fills (provider, ai, authored, dam, missing)
  // ignore the modality field.
  const chipKey =
    coverage.fillSource === 'doc' && coverage.extractionModality === 'vision'
      ? 'doc-vision'
      : coverage.fillSource;
  const meta = FILL_SOURCE_CHIPS[chipKey] ?? FILL_SOURCE_CHIPS.missing;

  // Confidence presentation guard. The resolver populates 0..1; clamp
  // to a finite percent for the chip. NaN / non-finite floor → omit
  // the % suffix rather than render `NaN%`.
  const confidencePct = Number.isFinite(coverage.confidence)
    ? Math.round(coverage.confidence * 100)
    : null;

  // Per-tone label suffix.
  let labelSuffix = '';
  if (coverage.fillSource === 'ai-inference' && confidencePct !== null) {
    labelSuffix = ` ${confidencePct}%`;
  } else if (coverage.fillSource === 'provider' && (coverage.sourceRef ?? '').startsWith('provider:')) {
    // sourceRef shape: `provider:<providerId>`. Show the provider id
    // (e.g. `fema-flood`) inline — gives the user a glanceable cite
    // without expanding the tooltip.
    const providerId = (coverage.sourceRef ?? '').slice('provider:'.length);
    if (providerId.length > 0) labelSuffix = ` · ${providerId}`;
  }

  // Tooltip: rationale + sourceRef. Both come from the resolver — the
  // chip NEVER fabricates a rationale (HARD RULE 8). Truncate the
  // sourceRef for the visible tooltip line to keep the hover usable
  // while still preserving the full string in the underlying envelope
  // (the lineage Inspector reads it back).
  const sourceRef = coverage.sourceRef ?? '';
  const trimmedSourceRef =
    sourceRef.length > 80
      ? `${sourceRef.slice(0, 77)}…`
      : sourceRef;
  const tooltipParts = [coverage.rationale];
  if (trimmedSourceRef) tooltipParts.push(`Source: ${trimmedSourceRef}`);
  if (coverage.authoredOverride) {
    tooltipParts.push(
      `Authored override active (by ${coverage.authoredOverride.authorUid}).`,
    );
  }
  const tooltip = tooltipParts.join(' · ');

  return (
    <span
      className="inline-flex items-center gap-1"
      data-testid={`fill-source-${reqId}`}
      data-tone={meta.tone}
      data-fill-source={coverage.fillSource}
      {...(coverage.extractionModality
        ? { 'data-extraction-modality': coverage.extractionModality }
        : {})}
    >
      <span
        title={tooltip}
        className={`inline-flex items-center gap-0.5 px-1 py-0.5 border type-body font-mono leading-none ${meta.toneClass}`}
      >
        <span aria-hidden="true">{meta.icon}</span>
        <span className="uppercase tracking-widest">
          {meta.label}
          {labelSuffix}
        </span>
      </span>
      {onUpgradeByUploading && shouldOfferUpgrade(coverage) && (
        <button
          type="button"
          onClick={() => onUpgradeByUploading(reqId)}
          className="type-body uppercase tracking-widest text-text-secondary hover:text-text-primary hover:underline"
          data-testid={`fill-source-upgrade-${reqId}`}
          title="Upload the source document to upgrade this row to a 'doc' fill."
        >
          Upgrade by uploading…
        </button>
      )}
    </span>
  );
};

export default CoverageCellFillSource;
